import { useEffect, useRef, useState, useMemo } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import type { DataType, DataZoomEvent, WeightedDataPoint, ZoomState } from "@/types/coinglass";
import { DATA_TYPE_CONFIG } from "@/constants/coinglass/coinglass";
import { useTokenDerivatives } from "@/contexts/token";
import { aggregateDataWeightedFundingRate, getAggregationLevel2 } from "@/utils/coinglass";
import { buildGrid, buildTimeAxis, buildValueAxis, buildInsideZoom } from "@/utils/charts/options";
import { formatPct, formatPrice } from "@/utils/charts/formatters";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { ChartSkeleton } from "@/components/charts";

interface TokenWeightedFundingRateChartProps {
  dataType: DataType;
  tokenId?: string;
  className?: string;
  hideTitle?: boolean;
  containerClassName?: string;
  externalRangeDays?: number | null;
}

function TokenWeightedFundingRateChart({
  dataType,
  tokenId = "BTC",
  className = "",
  hideTitle = false,
  containerClassName,
  externalRangeDays,
}: TokenWeightedFundingRateChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const rawDataRef = useRef<WeightedDataPoint[]>([]);
  const isUpdatingRef = useRef<boolean>(false);
  const lastZoomRef = useRef<ZoomState>({ start: 0, end: 100, aggregation: 1 });

  const [chartReady, setChartReady] = useState(false);

  const config = DATA_TYPE_CONFIG[dataType];

  // Consume data from context
  const { fundingOiWeighted, fundingVolWeighted, isLoading, isError, error } = useTokenDerivatives();
  
  // Select the appropriate series based on dataType
  const series = dataType === 'oiw' ? fundingOiWeighted.series : fundingVolWeighted.series;

  // Convert context series to chart format
  const chartData = useMemo<WeightedDataPoint[]>(() => {
    return series.map(point => ({
      timestamp: point.timestamp,
      fundingRate: point.rate,
      price: point.price ?? null,
    }));
  }, [series]);

  // Update raw data ref when data changes
  useEffect(() => {
    if (chartData.length > 0) {
      rawDataRef.current = chartData;
      setChartReady(true);
    }
  }, [chartData]);

  // Initialize chart
  useEffect(() => {
    if (isLoading || !chartReady || !chartRef.current || rawDataRef.current.length === 0) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const totalPoints = rawDataRef.current.length;
    const desiredDays = externalRangeDays ?? 180;
    const startPercentage = Math.max(0, ((totalPoints - desiredDays) / totalPoints) * 100);
    const initialAggregation = getAggregationLevel2(totalPoints, 100 - startPercentage);
    const aggregatedData = aggregateDataWeightedFundingRate(rawDataRef.current, initialAggregation);

    const prices = aggregatedData.map((d) => [d.timestamp, d.price] as [number, number | null]);
    const fundingRates = aggregatedData.map((d) => [d.timestamp, d.fundingRate] as [number, number]);
    const positiveRates = aggregatedData.map((d) => [d.timestamp, d.fundingRate >= 0 ? d.fundingRate : 0] as [number, number]);
    const negativeRates = aggregatedData.map((d) => [d.timestamp, d.fundingRate < 0 ? d.fundingRate : 0] as [number, number]);

    const option = {
      backgroundColor: "transparent",
      ...(hideTitle ? {} : { title: { text: `${tokenId} ${config.title}`, left: "center", top: 10, textStyle: { color: "#f3f4f6", fontSize: 18, fontWeight: "bold" } } }),
      grid: buildGrid({ left: 16, right: 24, top: hideTitle ? 40 : 56, bottom: 24, containLabel: true }),
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        borderWidth: 1,
        textStyle: { color: "#f3f4f6" },
        formatter: (params: unknown) => {
          try {
            const arr = (params as Array<{ seriesName: string; value: unknown; color: string }>) || [];
            const rawFirst = arr[0]?.value as unknown;
            const ts = Array.isArray(rawFirst) ? Number(rawFirst[0]) : NaN;
            const dateStr = Number.isFinite(ts) ? formatDateLabel(ts, 'medium') : '';
            const getVal = (v: unknown) => Array.isArray(v) ? Number(v[1]) : Number(v as number);
            const rateParam = arr.find(p => p.seriesName === 'Rate Line');
            const priceParam = arr.find(p => p.seriesName === `${tokenId} Price`);
            const r = getVal(rateParam?.value);
            const price = getVal(priceParam?.value);
            const rateColor = Number(r) >= 0 ? '#10b981' : '#ef4444';
            const priceColor = '#fbbf24';
            let result = `<div style="padding: 4px;">`;
            result += `<div style="font-size:11px;font-weight:500;margin-bottom:8px;color:#9ca3af;">${dateStr}</div>`;
            result += `<div style="margin: 4px 0; display: flex; justify-content: space-between; align-items: center; min-width: 200px;">`;
            result += `<span style="display: flex; align-items: center;"><span style="display: inline-block; width: 10px; height: 10px; background-color: ${rateColor}; margin-right: 8px; border-radius: 2px;"></span><span style="color: #d1d5db;">${config.labelShort} Rate</span></span>`;
            result += `<span style="margin-left: 20px; font-weight: 600; color: ${rateColor};">${Number.isFinite(r) ? r.toFixed(3) : '0.000'}%</span>`;
            result += `</div>`;
            result += `<div style="margin: 4px 0; display: flex; justify-content: space-between; align-items: center;">`;
            result += `<span style="display: flex; align-items: center;"><span style="display: inline-block; width: 10px; height: 10px; background-color: ${priceColor}; margin-right: 8px; border-radius: 2px;"></span><span style="color: #d1d5db;">${tokenId} Price</span></span>`;
            result += `<span style="margin-left: 20px; font-weight: 600; color: #fbbf24;">${Number.isFinite(price) ? `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</span>`;
            result += `</div>`;
            result += `</div>`;
            return result;
          } catch (err) {
            console.error('Tooltip formatter error:', err);
            return '';
          }
        },
      },
      xAxis: buildTimeAxis(),
      yAxis: [
        buildValueAxis("Funding Rate (%)", { position: "left", formatter: (v) => formatPct(3)(typeof v === 'string' ? parseFloat(v) : v) }),
        { ...buildValueAxis("Price", { position: "right", formatter: (v) => formatPrice(typeof v === 'string' ? parseFloat(v) : v) }), splitLine: { show: false } },
      ],
      dataZoom: [{ ...buildInsideZoom()[0], start: startPercentage, end: 100 }],
      series: [
        { name: `${config.labelShort} Rate`, type: "line", yAxisIndex: 0, data: positiveRates, smooth: false, symbol: "none", lineStyle: { width: 0 }, areaStyle: { color: "rgba(16, 185, 129, 0.35)" }, stack: "fundingRate" },
        { name: "Negative Rate", type: "line", yAxisIndex: 0, data: negativeRates, smooth: false, symbol: "none", lineStyle: { width: 0 }, areaStyle: { color: "rgba(239, 68, 68, 0.35)" }, stack: "fundingRate" },
        { name: "Rate Line", type: "line", yAxisIndex: 0, data: fundingRates, smooth: false, symbol: "none", lineStyle: { width: 1.5, color: "#6b7280" } },
        { name: `${tokenId} Price`, type: "line", yAxisIndex: 1, data: prices, smooth: false, symbol: "none", lineStyle: { color: "#fbbf24", width: 2 }, itemStyle: { color: "#fbbf24" } },
      ],
    } as EChartsOption;

    chart.setOption(option);
    lastZoomRef.current.aggregation = initialAggregation;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleDataZoom = (event: unknown) => {
      if (isUpdatingRef.current) return;
      const zoomEvent = event as DataZoomEvent;
      const start = zoomEvent.start ?? zoomEvent.batch?.[0]?.start ?? 0;
      const end = zoomEvent.end ?? zoomEvent.batch?.[0]?.end ?? 100;
      if (start === lastZoomRef.current.start && end === lastZoomRef.current.end) return;
      lastZoomRef.current = { start, end, aggregation: lastZoomRef.current.aggregation };
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const visiblePercentage = end - start;
        const newAggregation = getAggregationLevel2(rawDataRef.current.length, visiblePercentage);
        if (newAggregation === lastZoomRef.current.aggregation) return;
        lastZoomRef.current.aggregation = newAggregation;
        const newAggregatedData = aggregateDataWeightedFundingRate(rawDataRef.current, newAggregation);
        const newPrices = newAggregatedData.map((d) => [d.timestamp, d.price] as [number, number | null]);
        const newRates = newAggregatedData.map((d) => [d.timestamp, d.fundingRate] as [number, number]);
        const newPositiveRates = newAggregatedData.map((d) => [d.timestamp, d.fundingRate >= 0 ? d.fundingRate : 0] as [number, number]);
        const newNegativeRates = newAggregatedData.map((d) => [d.timestamp, d.fundingRate < 0 ? d.fundingRate : 0] as [number, number]);
        isUpdatingRef.current = true;
        chart.setOption({ series: [{ data: newPositiveRates }, { data: newNegativeRates }, { data: newRates }, { data: newPrices }] }, { notMerge: false });
        setTimeout(() => { isUpdatingRef.current = false; }, 100);
      }, 250);
    };
    chart.on("dataZoom", handleDataZoom);
    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
      chart.off("dataZoom", handleDataZoom);
      chart.dispose();
    };
  }, [tokenId, dataType, isLoading, chartReady, config, hideTitle, externalRangeDays]);

  // Handle external range changes
  useEffect(() => {
    if (!chartInstanceRef.current || !rawDataRef.current.length) return;
    if (externalRangeDays == null) {
      isUpdatingRef.current = true;
      chartInstanceRef.current.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
      setTimeout(() => { isUpdatingRef.current = false; }, 100);
      return;
    }
    const totalPoints = rawDataRef.current.length;
    const start = Math.max(0, 100 - (externalRangeDays / totalPoints) * 100);
    isUpdatingRef.current = true;
    chartInstanceRef.current.dispatchAction({ type: 'dataZoom', start, end: 100 });
    setTimeout(() => { isUpdatingRef.current = false; }, 100);
  }, [externalRangeDays, dataType]);

  if (isLoading) {
    return <ChartSkeleton height={360} />;
  }

  if (isError) {
    return (
      <div className="flex h-[360px] items-center justify-center rounded-xl bg-gray-800/40 p-4">
        <div className="text-center">
          <p className="mb-2 text-xl font-semibold text-red-400">Error Loading Data</p>
          <p className="text-gray-400">{error?.message || "Failed to load funding rate data"}</p>
        </div>
      </div>
    );
  }

  if (!chartReady || chartData.length === 0) {
    return <ChartSkeleton height={360} />;
  }

  return (
    <div className={`w-full ${className}`}>
      {!hideTitle && (
        <h2 className="mb-4 text-2xl font-bold text-white">
          {tokenId} {config.title}
        </h2>
      )}
      <div ref={chartRef} className={containerClassName ?? "h-[500px] w-full rounded-lg bg-gray-900 p-4"} />
    </div>
  );
}

export default TokenWeightedFundingRateChart;
